import React from 'react';
import { Link } from 'react-router-dom';
import StatusBadge from '../../../../components/status/StatusBadge';
import { Briefcase, Landmark, Wallet, Target, Activity } from 'lucide-react';
import { motion } from 'framer-motion';

export default function BlocEngagements({ data }) {
    const formatEuro = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

    const participations = data.transactionParticipations || [];

    const investissements = participations.filter(p => p.role === 'Investisseur' || p.role === 'Prêteur');
    const financements = participations.filter(p => p.role === 'Emprunteur' || p.role === 'Porteur');

    return (
        <div className="space-y-8">
            {/* Mes Investissements */}
            <div className="bg-surface rounded-xl shadow border border-workspace overflow-hidden">
                <div className="px-6 py-4 border-b border-workspace flex items-center justify-between bg-info-50/50 dark:bg-info-900/10">
                    <h3 className="text-lg font-bold text-info-700 dark:text-info-400 flex items-center gap-2">
                        <Wallet className="w-5 h-5" />
                        Mes Investissements & Prêts
                    </h3>
                </div>
                
                {investissements.length === 0 ? (
                    <div className="p-6 text-subtle text-center italic text-sm">Aucun investissement en cours.</div>
                ) : (
                    <ul className="divide-y divide-workspace">
                        {investissements.map(p => (
                            <li key={p.id} className="p-6 hover:bg-surface-muted transition-colors flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div className="flex-1">
                                    <p className="text-sm font-bold text-primary flex items-center gap-2 mb-1">
                                        <Link to={`/transactions/${p.transactionId}`} className="hover:text-info-600 transition-colors">
                                            {p.transaction?.title || 'Opération'}
                                        </Link>
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-subtle">
                                        <span className="font-semibold px-2 py-0.5 bg-info-100 text-info-700 dark:bg-info-900/40 dark:text-info-300 rounded-full">{p.role}</span>
                                        <span>•</span>
                                        <span className="font-mono">{p.transaction?.reference}</span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap sm:flex-nowrap gap-4 sm:gap-8 items-center w-full sm:w-auto">
                                    <div className="flex flex-col text-left sm:text-right">
                                        <span className="text-[10px] uppercase font-bold text-tertiary tracking-wider">Engagé</span>
                                        <span className="text-sm font-semibold text-primary">{formatEuro(p.committedAmount || p.investedAmount)}</span>
                                    </div>
                                    <div className="flex flex-col text-left sm:text-right">
                                        <span className="text-[10px] uppercase font-bold text-tertiary tracking-wider">Appelé</span>
                                        <span className="text-sm font-semibold text-primary">{formatEuro(p.calledAmount || p.investedAmount)}</span>
                                    </div>
                                    <div className="shrink-0">
                                        <StatusBadge status={p.transaction?.status} type="transaction" />
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Mes Financements */}
            <div className="bg-surface rounded-xl shadow border border-workspace overflow-hidden">
                <div className="px-6 py-4 border-b border-workspace flex items-center justify-between bg-success-50/50 dark:bg-success-900/10">
                    <h3 className="text-lg font-bold text-success-700 dark:text-success-400 flex items-center gap-2">
                        <Landmark className="w-5 h-5" />
                        Mes Financements & Dettes
                    </h3>
                </div>
                
                {financements.length === 0 ? (
                    <div className="p-6 text-subtle text-center italic text-sm">Aucun financement en cours.</div>
                ) : (
                    <ul className="divide-y divide-workspace">
                        {financements.map(p => (
                            <li key={p.id} className="p-6 hover:bg-surface-muted transition-colors flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div className="flex-1">
                                    <p className="text-sm font-bold text-primary flex items-center gap-2 mb-1">
                                        <Link to={`/transactions/${p.transactionId}`} className="hover:text-success-600 transition-colors">
                                            {p.transaction?.title || 'Opération'}
                                        </Link>
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-subtle">
                                        <span className="font-semibold px-2 py-0.5 bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-300 rounded-full">{p.role}</span>
                                        <span>•</span>
                                        <span className="font-mono">{p.transaction?.reference}</span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap sm:flex-nowrap gap-4 sm:gap-8 items-center w-full sm:w-auto">
                                    <div className="flex flex-col text-left sm:text-right">
                                        <span className="text-[10px] uppercase font-bold text-tertiary tracking-wider">Montant</span>
                                        <span className="text-sm font-semibold text-primary">{formatEuro(p.committedAmount || p.investedAmount)}</span>
                                    </div>
                                    <div className="flex flex-col text-left sm:text-right">
                                        <span className="text-[10px] uppercase font-bold text-tertiary tracking-wider">Encours</span>
                                        <span className="text-sm font-semibold text-primary">{formatEuro(p.outstandingAmount || p.investedAmount)}</span>
                                    </div>
                                    <div className="shrink-0">
                                        <StatusBadge status={p.transaction?.status} type="transaction" />
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
